import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';
import type { FieldValues } from '@atlassian/proforma-form-renderer/field-values';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';

import {
	useProjectId,
	useReporterAccountId,
	useRequestTypeId,
} from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import type { CmdbAutocompleteResponse } from './types';
import { requestBody } from './utils';

export const useCmdbObjectsService = () => {
	di(useProjectId, useReporterAccountId, useRequestTypeId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const projectId = useProjectId();
	const reporterAccountId = useReporterAccountId();
	const requestTypeId = useRequestTypeId();

	const request = useCallback(
		({
			currentValues,
			jiraFieldKey,
			proformaForm,
			query,
			start = 0,
		}: {
			currentValues: FieldValues;
			jiraFieldKey: string;
			proformaForm: ProformaForm;
			query: string;
			start?: number;
		}) =>
			proformaGasAnalyticsFetch<CmdbAutocompleteResponse>(
				`/rest/servicedesk/cmdb/1/customer/proforma/field/${jiraFieldKey}/autocomplete?start=${start}`,
				{
					method: 'POST',
					body: requestBody(
						projectId,
						requestTypeId,
						currentValues,
						proformaForm,
						query,
						reporterAccountId,
					),
				},
				createAnalyticsEvent,
				errorUtils,
				'json',
				'cmdbAutocomplete',
			),
		[projectId, reporterAccountId, requestTypeId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
